import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { doc, setDoc, getDoc, getDocs, query, collection, where, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';

const UserContext = createContext();
const KALU_USER_ID = 'Qu5CbNL3hpbLgqt6v1Zu6jchwLP2';

function generateRandomUsername() {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let suffix = '';
  for (let i = 0; i < 5; i++) suffix += chars[Math.floor(Math.random() * chars.length)];
  return 'lecteur_' + suffix;
}

function detectDeviceAndBrowser() {
  const ua = navigator.userAgent;
  const device = /Mobi|Android|iPhone|iPad/i.test(ua)
    ? (/iPad/i.test(ua) ? 'tablet' : 'mobile')
    : 'desktop';
  const browser = /Edg/i.test(ua) ? 'Edge'
    : /OPR|Opera/i.test(ua) ? 'Opera'
    : /Chrome/i.test(ua) ? 'Chrome'
    : /Safari/i.test(ua) ? 'Safari'
    : /Firefox/i.test(ua) ? 'Firefox'
    : 'Autre';
  return { device, browser };
}

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsUsername, setNeedsUsername] = useState(false);
  const userRef = useRef(null);

  const setUserIfChanged = (newUser) => {
    if (!newUser) {
      if (userRef.current !== null) {
        userRef.current = null;
        setUser(null);
      }
      return;
    }
    const prev = userRef.current;
    if (prev && prev.id === newUser.id && JSON.stringify(prev) === JSON.stringify(newUser)) {
      return;
    }
    userRef.current = newUser;
    setUser(newUser);
  };

  const autoFollowKalu = async (newUserId) => {
    try {
      await setDoc(doc(db, 'social', newUserId), {
        following: [KALU_USER_ID],
      }, { merge: true });
    } catch (e) {
      console.error('Erreur auto-follow KALU:', e);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userDocSnap = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (userDocSnap.exists()) {
          const isPasswordProvider = firebaseUser.providerData[0]?.providerId === 'password';
          if (isPasswordProvider && !firebaseUser.emailVerified) {
            setUserIfChanged({
              id: firebaseUser.uid,
              ...userDocSnap.data(),
              emailVerified: false,
              pendingVerification: true,
            });
          } else {
            if (firebaseUser.emailVerified && !userDocSnap.data().emailVerified) {
              setDoc(doc(db, 'users', firebaseUser.uid), { emailVerified: true }, { merge: true }).catch(() => {});
            }
            setUserIfChanged({
              id: firebaseUser.uid,
              ...userDocSnap.data(),
              emailVerified: firebaseUser.emailVerified,
            });
          }
        } else {
          const userData = {
            name: firebaseUser.displayName || firebaseUser.email.split('@')[0],
            email: firebaseUser.email,
            username: generateRandomUsername(),
            bio: '',
            authorId: 'author_' + firebaseUser.uid,
            avatar: firebaseUser.photoURL || null,
            provider: firebaseUser.providerData[0]?.providerId || 'password',
            premium: false,
            premiumUntil: null,
            emailVerified: firebaseUser.emailVerified,
            createdAt: new Date().toISOString(),
          };
          await setDoc(doc(db, 'users', firebaseUser.uid), userData);
          await setDoc(doc(db, 'userLibraries', firebaseUser.uid), {
            library: [],
            readingProgress: {},
            swipedBooks: [],
            plumes: 0,
          });
          await autoFollowKalu(firebaseUser.uid);

          const isPasswordProvider = firebaseUser.providerData[0]?.providerId === 'password';
          if (isPasswordProvider && !firebaseUser.emailVerified) {
            setUserIfChanged({
              id: firebaseUser.uid,
              ...userData,
              emailVerified: false,
              pendingVerification: true,
            });
          } else {
            setUserIfChanged({ id: firebaseUser.uid, ...userData, emailVerified: firebaseUser.emailVerified });
            if (!isPasswordProvider) setNeedsUsername(true);
          }
        }
      } else {
        setUserIfChanged(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Statut en ligne + device/browser
  useEffect(() => {
    if (!user || user.pendingVerification) return;

    const onlineRef = doc(db, 'users', user.id);
    const { device, browser } = detectDeviceAndBrowser();

    setDoc(onlineRef, { isOnline: true, lastSeen: serverTimestamp(), device, browser }, { merge: true }).catch(() => {});

    const heartbeat = setInterval(() => {
      setDoc(onlineRef, { isOnline: true, lastSeen: serverTimestamp(), device, browser }, { merge: true }).catch(() => {});
    }, 60000);

    const handleBeforeUnload = () => {
      setDoc(onlineRef, { isOnline: false, lastSeen: serverTimestamp() }, { merge: true }).catch(() => {});
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setDoc(onlineRef, { isOnline: false, lastSeen: serverTimestamp() }, { merge: true }).catch(() => {});
      } else {
        setDoc(onlineRef, { isOnline: true, lastSeen: serverTimestamp(), device, browser }, { merge: true }).catch(() => {});
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(heartbeat);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      setDoc(onlineRef, { isOnline: false, lastSeen: serverTimestamp() }, { merge: true }).catch(() => {});
    };
  }, [user?.id, user?.pendingVerification]);

  const register = async (name, username, email, password) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, { displayName: name });
    try {
      await fetch('https://us-central1-plumly-fe6f7.cloudfunctions.net/sendVerificationEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name }),
      });
    } catch (e) {
      console.warn('Verification email failed:', e);
    }

    const userData = {
      name,
      username: username.toLowerCase(),
      email,
      bio: '',
      authorId: 'author_' + result.user.uid,
      avatar: null,
      provider: 'password',
      premium: false,
      premiumUntil: null,
      emailVerified: false,
      createdAt: new Date().toISOString(),
    };
    await setDoc(doc(db, 'users', result.user.uid), userData);
    await setDoc(doc(db, 'userLibraries', result.user.uid), {
      library: [],
      readingProgress: {},
      swipedBooks: [],
      plumes: 0,
    });
    await autoFollowKalu(result.user.uid);
  };

  const login = async (email, password) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  };

  const loginWithApple = async () => {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    await signInWithPopup(auth, provider);
  };

  const resendVerificationEmail = async () => {
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      try {
        await fetch('https://us-central1-plumly-fe6f7.cloudfunctions.net/sendVerificationEmail', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: auth.currentUser.email,
            name: auth.currentUser.displayName || '',
          }),
        });
      } catch (e) {
        console.warn('Resend verification email failed:', e);
      }
    }
  };

  const checkUsernameAvailable = async (username) => {
    if (!username || username.length < 3) return false;
    const q = query(collection(db, 'users'), where('username', '==', username.toLowerCase()));
    const snap = await getDocs(q);
    if (snap.empty) return true;
    if (snap.docs.length === 1 && snap.docs[0].id === user?.id) return true;
    return false;
  };

  const logout = async () => {
    if (user && !user.pendingVerification) {
      await setDoc(doc(db, 'users', user.id), {
        isOnline: false,
        lastSeen: serverTimestamp(),
      }, { merge: true }).catch(() => {});
    }
    await signOut(auth);
    setUser(null);
  };

  const updateUser = async (updates) => {
    if (!user) return;
    await setDoc(doc(db, 'users', user.id), { ...user, ...updates }, { merge: true });
    setUser(prev => ({ ...prev, ...updates }));
  };

  return (
    <UserContext.Provider value={{
      user, loading, register, login, logout, updateUser,
      loginWithGoogle, loginWithApple,
      resendVerificationEmail, checkUsernameAvailable,
      needsUsername, setNeedsUsername,
    }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}